
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProfileBasicInfoProps {
  formData: {
    display_name: string;
    bio: string;
    date_of_birth: string;
    show_age: boolean;
    height: string;
    weight: string;
    body_type: string;
    position: string;
    city: string;
  };
  onInputChange: (field: string, value: string | boolean) => void;
  loading: boolean;
}


const ProfileBasicInfo = ({
  formData,
  onInputChange,
  loading
}: ProfileBasicInfoProps) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="display_name" className="text-ice-white">Display Name *</Label>
          <Input 
            id="display_name" 
            value={formData.display_name} 
            onChange={e => onInputChange('display_name', e.target.value)} 
            placeholder="Enter your display name" 
            disabled={loading} 
            className="bg-carbon border-ice-white/20 text-ice-white" 
          />
        </div>

        <div>
          <Label htmlFor="date_of_birth" className="text-ice-white">Date of Birth *</Label>
          <Input 
            id="date_of_birth" 
            type="text" 
            inputMode="numeric" 
            pattern="[0-9.]*" 
            placeholder="DD.MM.YYYY" 
            value={formData.date_of_birth} 
            onChange={e => {
              let value = e.target.value.replace(/[^\d.]/g, '');

              // Auto-format as DD.MM.YYYY while typing
              if (value.length <= 2) {
                // Just the day
              } else if (value.length <= 4) {
                // Add first dot after day
                value = value.slice(0, 2) + '.' + value.slice(2);
              } else if (value.length <= 8) {
                // Add second dot after month
                value = value.slice(0, 2) + '.' + value.slice(2, 4) + '.' + value.slice(4);
              }

              // Limit to DD.MM.YYYY format
              if (value.length > 10) {
                value = value.slice(0, 10);
              }
              onInputChange('date_of_birth', value);
            }} 
            disabled={loading} 
            className="bg-carbon border-ice-white/20 text-ice-white my-[4px]" 
          />
        </div>
      </div>

      <div>
        <Label htmlFor="city" className="text-ice-white">Living in:</Label>
        <Input 
          id="city" 
          value={formData.city} 
          onChange={e => onInputChange('city', e.target.value)} 
          placeholder="Enter your city" 
          disabled={loading} 
          className="bg-carbon border-ice-white/20 text-ice-white" 
        />
      </div>

      <div className="flex items-center space-x-2">
        <Switch 
          id="show_age" 
          checked={formData.show_age} 
          onCheckedChange={checked => onInputChange('show_age', checked)} 
          disabled={loading} 
        />
        <Label htmlFor="show_age" className="text-ice-white">Show age on profile</Label>
      </div>

      <div>
        <Label htmlFor="bio" className="text-ice-white">Bio</Label>
        <Textarea 
          id="bio" 
          value={formData.bio} 
          onChange={e => onInputChange('bio', e.target.value)} 
          placeholder="Tell us about yourself..." 
          disabled={loading} 
          className="bg-carbon border-ice-white/20 text-ice-white min-h-[100px]" 
        />
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="height" className="text-ice-white">Height (cm)</Label>
          <Input 
            id="height" 
            type="number" 
            value={formData.height} 
            onChange={e => onInputChange('height', e.target.value)} 
            placeholder="Enter your height" 
            disabled={loading} 
            className="bg-carbon border-ice-white/20 text-ice-white" 
          />
        </div>

        <div>
          <Label htmlFor="weight" className="text-ice-white">Weight (kg)</Label>
          <Input 
            id="weight" 
            type="number" 
            value={formData.weight} 
            onChange={e => onInputChange('weight', e.target.value)} 
            placeholder="Enter your weight" 
            disabled={loading} 
            className="bg-carbon border-ice-white/20 text-ice-white" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="body_type" className="text-ice-white">Body Type</Label>
          <Select value={formData.body_type} onValueChange={value => onInputChange('body_type', value)} disabled={loading}>
            <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
              <SelectValue placeholder="Select body type" />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-ice-white/20">
              <SelectItem value="slim">Slim</SelectItem>
              <SelectItem value="athletic">Athletic</SelectItem>
              <SelectItem value="average">Average</SelectItem>
              <SelectItem value="muscular">Muscular</SelectItem>
              <SelectItem value="curvy">Curvy</SelectItem>
              <SelectItem value="plus-size">Plus Size</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="position" className="text-ice-white">Position</Label>
          <Select value={formData.position} onValueChange={value => onInputChange('position', value)} disabled={loading}>
            <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
              <SelectValue placeholder="Select position" />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-ice-white/20">
              <SelectItem value="top">Top</SelectItem>
              <SelectItem value="bottom">Bottom</SelectItem>
              <SelectItem value="vers">Versatile</SelectItem>
              <SelectItem value="side">Side</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default ProfileBasicInfo;
